import styled, { DefaultTheme, useTheme } from 'styled-components';
import { AlertVariation } from './legacy/Alert';
import { Avatar } from '../Avatar/Avatar';
import { PropsWithChildren, useMemo } from 'react';
import { Heading } from '../Heading/Heading';
import { P } from '..';

const variationIcons: { [key in AlertVariation]: string } = {
  info: 'info-circle',
  warning: 'exclamation-triangle',
  error: 'ban',
  success: 'check',
};

const getVariationColours = (theme: DefaultTheme): { [key in AlertVariation]: string } => ({
  info: theme.vars.newColor.darkGS2,
  warning: theme.vars.newColor.orange2,
  error: theme.vars.newColor.red2,
  success: theme.vars.newColor.green2,
});

const Border = styled.div`
  width: 288px;
  height: 131px;
  border: 1px solid ${({ theme }) => theme.vars.newColor.lightGS1};
  display: flex;
  padding: 16px 14px;
`;

const IconArea = styled.div<Pick<WorkflowAlertProps, 'variation'>>`
  display: flex;
  align-items: center;
  padding: 15px;
  flex-flow: column;

  ${({ theme, variation }) => {
    const colour = getVariationColours(theme)[variation];

    return `
      margin: -16px 0 -16px -14px;
      border-left: 4px solid ${colour};
    `;
  }}
`;

export interface WorkflowAlertProps extends IntrinsicProps {
  /** The heading that will be displayed in the alert. */
  heading: string;
  /** Variation of the alert. Default variation: `info`. */
  variation?: AlertVariation;
}

/**
 * Alerts for workflows. They should only be used within workflow drag and drop
 * content boxes.
 *
 * @deprecated This component will be removed soon. Please use <Alert />.
 */
export const WorkflowAlert = ({ heading, children, variation, ...props }: PropsWithChildren<WorkflowAlertProps>) => {
  const theme = useTheme();
  const colour = useMemo(() => getVariationColours(theme)[variation || 'info'], [theme, variation]);
  return (
    <Border {...props} role="alert">
      <IconArea variation={variation}>
        <span className="workflow-alert-icon">
          <Avatar icon={variationIcons[variation]} bg={colour} />
        </span>
      </IconArea>
      <div>
        <Heading variation="h4" style={{ marginBottom: 8 }}>
          {heading}
        </Heading>
        <P color={theme.vars.newColor.darkGS2}>{children}</P>
      </div>
    </Border>
  );
};
